import { useCallback, useRef, useState } from 'react';
import { Container } from '@/components/Layout/Container';
import { SecondaryHeader } from '@/components/Layout/Header/SecondaryHeader';
import ShiftTable, { ShiftTableHandle } from '@/components/Fatigue/ShiftTable';
import { Sidebar } from '@/components/shared/Sidebar';
import { useSidebar } from '@/context/SidebarContext';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { saveFatigueCalculation } from '@/lib/supabase/fatigue';
import { DEFAULT_FRI_DEFAULTS } from '@/lib/fatigueCalculations';
import type { SavedCalculation } from '@/types/cvr';
import type { FatigueSavePayload } from '@/types/fatigue';

export function FatigueManagement() {
  const { isCollapsed } = useSidebar();
  const { toast } = useToast();
  const shiftTableRef = useRef<ShiftTableHandle | null>(null);
  const [snapshot, setSnapshot] = useState<FatigueSavePayload>({
    defaults: DEFAULT_FRI_DEFAULTS,
    rotating: false,
    useDefaultsOnly: true,
    duties: [],
  });

  const handleSnapshotChange = useCallback((payload: FatigueSavePayload) => {
    setSnapshot((prev) => ({
      ...payload,
      meta: prev?.meta,
    }));
  }, []);

  const handleSave = useCallback(
    async (name: string, data: FatigueSavePayload) => {
      try {
        if (!name?.trim()) {
          throw new Error('Name is required');
        }

        if (!data || typeof data !== 'object') {
          throw new Error('Invalid calculation data');
        }

        const savedCalculation = await saveFatigueCalculation(name, data);

        toast({
          title: 'Success',
          description: 'Schedule saved successfully',
        });

        return savedCalculation;
      } catch (error) {
        toast({
          title: 'Error',
          description:
            error instanceof Error ? error.message : 'Failed to save schedule',
          variant: 'destructive',
        });
        throw error;
      }
    },
    [toast]
  );

  const handleLoad = useCallback(
    (calculation: SavedCalculation) => {
      try {
        if (!calculation?.data || typeof calculation.data !== 'object') {
          throw new Error('Invalid schedule data');
        }

        const payload = calculation.data as FatigueSavePayload;
        shiftTableRef.current?.loadSavePayload(payload);
        setSnapshot(payload);

        toast({
          title: 'Success',
          description: 'Schedule loaded successfully',
        });
      } catch (error) {
        toast({
          title: 'Error',
          description:
            error instanceof Error ? error.message : 'Failed to load schedule',
          variant: 'destructive',
        });
      }
    },
    [toast]
  );

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SecondaryHeader />

      <div className="flex flex-1 flex-col md:flex-row">
        <Sidebar
          toolType="fatigue"
          data={snapshot}
          onSave={handleSave}
          onLoad={handleLoad}
          className={cn(
            'w-full overflow-y-auto border-b transition-all duration-300',
            'md:border-r md:border-b-0',
            isCollapsed ? 'md:w-20' : 'md:w-80'
          )}
          aria-label="Fatigue schedule sidebar"
          role="complementary"
        />

        <main
          className={cn(
            'flex-1 overflow-y-auto transition-all duration-300',
            isCollapsed ? 'md:ml-20' : 'md:ml-80'
          )}
          role="main"
        >
          <Container>
            <ShiftTable ref={shiftTableRef} onSnapshotChange={handleSnapshotChange} />
          </Container>
        </main>
      </div>
    </div>
  );
}
